<template>
    <ModalsBase modalWidth="max-w-[460px]">
        <template #header>
            <div class="text-center mb-8">
                <h2 class="mb-3">Upload Video</h2>
            </div>
        </template>
        <template #body>
            <form @submit.prevent="uploadVideos">
                <div class="mb-4">
                    <Input type="text" id="title" label="Title" placeholder="Enter video title" :disabled="uploadLoading":isRequired="true"  v-model="title" />
                </div>
                <div class="mb-4">
                    <Input id="file" type="file" lg="true" inputClassNew="flex-col text-center !gap-2 !py-5" :accept="'.mp4,.webm,.mkv,.hevc,.heif'" supported-files=".mp4, .webm, .mkv, .hevc, .heif" :supportedFileSize="maxSizeMB" :disabled="uploadLoading" :isRequired="true"  @files="handleFile" />
                </div>

                <!-- Unified File & Progress Card -->
                <div class="border border-[#D1D1D1] rounded-md p-3 mt-4" v-if="file">
                    
                    <!-- File Info Row -->
                    <div class="flex items-center justify-between gap-3">
                        <div class="flex items-center gap-3">
                            <div class="w-11 h-11 flex items-center justify-center bg-blue-50 rounded-full shrink-0 text-blue-500 shadow-sm border border-blue-100">
                                <Icon name="ph:video-camera-fill" size="22" />
                            </div>
                            <div class="min-w-0">
                                <p class="mb-1 leading-none text-sm font-medium text-inactiveTabs truncate">{{ truncateString(file.name,10,7,5) }}</p>
                                <p class="mb-0 leading-none text-sm text-themegray">{{ formatFileSize(file.size) }}</p>
                            </div>
                        </div>
                        <!-- Hide Remove button during upload so user doesn't click a dead button -->
                        <button v-if="!uploadLoading" type="button" class="bg-black text-white text-[11px] leading-none px-3 py-2 rounded-full shrink-0" @click="removeFile()">Remove</button>
                    </div>

                    <!-- Upload Progress Section -->
                    <div v-if="uploadLoading || finalizeFailed" class="mt-3 pt-3 border-t border-[#E0E0E0]">
                        <div class="flex items-center justify-between mb-2">
                            <p class="mb-0 text-sm font-medium text-inactiveTabs">
                                {{ uploadPhaseLabel }}
                            </p>
                            <p class="mb-0 text-xs font-semibold text-themegray">{{ uploadProgress }}%</p>
                        </div>
                        <div class="progress-wrap h-2 bg-[#E0E0E0] rounded-full mb-2 overflow-hidden">
                            <div class="progress bg-secondary h-full rounded-full transition-all duration-200" :style="{ width: `${uploadProgress}%` }"></div>
                        </div>
                        <p class="mb-0 text-xs text-themegray">{{ uploadStatusText }}</p>
                        <p v-if="finalizeFailed" class="mb-0 text-xs text-[#D32F2F] mt-1 font-medium">Finalizing failed. Click upload to retry finalizing.</p>
                    </div>

                </div>
 
                <!-- <div class="uploading-file border-t border-[#BDBDBD] pt-4">
                    <div
                        class="attachment-wrap flex items-center justify-between p-3 border border-[#D1D1D1] rounded-md mb-4 last:mb-0">
                        <div class="flex items-center gap-3 w-full">
                            <div class="icon">
                                <div class="w-11 h-11 flex items-center justify-center bg-[#F2F2F2] rounded-full">
                                    <img src="/images/docx_icon.svg" alt="">
                                </div>
                            </div>
                            <div class="attach-content w-full">
                                <div class="flex items-center justify-between">
                                    <p class="mb-1 leading-none text-sm text-inactiveTabs font-medium">Uploading...</p>
                                    <button class="mb-1 leading-none text-xs text-inactiveTabs font-medium">
                                        <Icon name='radix-icons:cross-1' size="10" />
                                    </button>
                                </div>
                                <div class="flex items-center justify-between mb-2">
                                    <p class="mb-0 leading-none text-xs text-themegray">Videoone.mp4 | Size: 24mb</p>
                                    <span class="mb-1 leading-none text-xs text-inactiveTabs font-medium">40%</span>
                                </div>
                                <div class="progress-wrap h-2 bg-[#E0E0E0] rounded-full">
                                    <div class="progress w-[40%] bg-secondary h-full rounded-full"></div>
                                </div>
                            </div>
                        </div>
                       
                    </div>
                </div> -->
                <div class="flex gap-4 mt-6">
                    <Button variant="outline" type="button" class="w-full" @click="closeModal()">CANCEL</Button>
                    <Button type="submit" class="w-full" :disabled="uploadLoading && !finalizeFailed" :loading="uploadLoading">
                        {{ finalizeFailed ? 'RETRY FINALIZE' : 'UPLOAD' }}
                    </Button>
                </div>
            </form>
        </template>
    </ModalsBase>
</template>

<script setup>

const emit = defineEmits(['close'])
const MAX_PART_RETRIES = 3
const PART_UPLOAD_CONCURRENCY = 4

const closeModal = async () => {
    if (uploadLoading.value || currentUploadSession.value?.uploadId) {
        await cancelMultipartUpload()
    }
    resetUploadState()
    emit('close', 'upload', false)
}

const { uploadMyVideo ,getMyVideos} = await useTrainingVideo()

const errorStore = useErrorStore()
const uploadLoading = ref(false)
const uploadPhase = ref('idle')
const uploadProgress = ref(0)
const uploadedBytes = ref(0)
const totalBytes = ref(0)
const finalizeFailed = ref(false)
const pendingCompletePayload = ref(null)
const currentUploadSession = ref({ uploadId: null, key: null })
const activePartAbortControllers = ref([])
const partProgressBytes = ref({})
const title = ref('')
const file = ref(null)

const acceptedMimeTypes = [
  'video/mp4',
  'video/webm',
  'video/x-matroska', // .mkv
  'video/mkv',
  'video/hevc',
  'video/x-hevc',
  'video/quicktime'
];
const maxSizeMB = 150; // 50 MB limit

function validateFile(selectedFile, acceptedMimeTypes, maxSizeMB) {

  // Get the file size in MB
  const fileSizeMB = selectedFile.size / (1024 * 1024);

  // Check if the file type (MIME type) is valid
  if (!acceptedMimeTypes.includes(selectedFile.type)) {
    file.value = null
    errorStore.showError(`Invalid file type. Accepted types are: ${acceptedMimeTypes.join(', ')}`,true,'warning')
    return false;
  }

  // Check if the file size is within the allowed limit
  if (fileSizeMB > maxSizeMB) {
    file.value = null
    errorStore.showError(`File size exceeds the maximum limit of ${maxSizeMB} MB`,true,'warning')
    return false;
  }

  // If all checks pass, return true
  return true;
}

const handleFile = (f) =>{

    console.log("Selected File : ",f)
    if(!f){
        alert("Please select a file.")
        return
    }
    if(validateFile(f[0],acceptedMimeTypes,maxSizeMB)){
        file.value = f[0]
    }
    return
}

const uploadVideos = async() => {
    if (finalizeFailed.value && pendingCompletePayload.value) {
        await retryFinalizeUpload()
        return
    }

    try {
        console.log("File : ",file.value)
        if(!file.value){
            errorStore.showError('Please Select File.',true,'warning')
            return
        }
        uploadLoading.value = true
        finalizeFailed.value = false
        uploadProgress.value = 0
        uploadedBytes.value = 0
        partProgressBytes.value = {}
        await uploadVideoUnified(file.value, title.value)
        await handleUploadSuccess()

    } catch (error) {
        console.log("Error : ",error)
        if (error?.name === 'AbortError') {
            errorStore.showError('Upload cancelled.',true,'warning')
            return
        }
        if (currentUploadSession.value?.uploadId && !finalizeFailed.value) {
            await cancelMultipartUpload()
        }
        if (!finalizeFailed.value) {
            errorStore.showError(error?.message || 'Video upload failed.',true,'error')
        }
    }
    finally{
        if (!finalizeFailed.value) {
            uploadLoading.value = false
        }
    }
}

const uploadVideoUnified = async (selectedFile, videoTitle) => {
    uploadPhase.value = 'uploading'
    totalBytes.value = selectedFile.size
    uploadedBytes.value = 0
    partProgressBytes.value = {}
    updateUploadProgress()

    let initData = null
    try {
        const initRes = await useAxiosPost('media', {
            uploadStage: 'init',
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            mimeType: selectedFile.type || 'application/octet-stream',
            title: videoTitle
        })
        initData = initRes?.data || initRes
    } catch (error) {
        initData = null
    }

    const { uploadId, key, partSize, parts = [] } = initData || {}
    const canUseMultipart =
        !!uploadId &&
        !!key &&
        !!partSize &&
        Array.isArray(parts) &&
        parts.length > 0

    if (!canUseMultipart) {
        await uploadSingleRequest(selectedFile, videoTitle)
        uploadProgress.value = 100
        return
    }

    currentUploadSession.value = { uploadId, key }

    const completedParts = await uploadPartsInParallel({
        parts,
        selectedFile,
        partSize,
        mimeType: selectedFile.type || 'application/octet-stream'
    })

    uploadPhase.value = 'finalizing'

    const completePayload = {
        uploadStage: 'complete',
        uploadId,
        key,
        parts: completedParts,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type || 'application/octet-stream',
        title: videoTitle
    }

    pendingCompletePayload.value = completePayload
    await finalizeMultipartUpload(completePayload)
}

const uploadSingleRequest = async (selectedFile, videoTitle) => {
    let formData = new FormData()
    formData.append('title', videoTitle)
    formData.append('bucket', "media")
    formData.append('size', selectedFile.size)
    formData.append('file', selectedFile)
    await uploadMyVideo(formData)
}

const uploadPartsInParallel = async ({ parts, selectedFile, partSize, mimeType }) => {
    const completedParts = new Array(parts.length)
    let nextIndex = 0

    const uploadNext = async () => {
        while (nextIndex < parts.length) {
            const currentIndex = nextIndex
            nextIndex += 1

            const part = parts[currentIndex]
            const partNumber = Number(part.partNumber)
            const start = (partNumber - 1) * partSize
            const end = Math.min(start + partSize, selectedFile.size)
            const chunk = selectedFile.slice(start, end)
            partProgressBytes.value[partNumber] = 0

            const etag = await uploadPartWithRetry(part.url, chunk, mimeType, partNumber)
            completedParts[currentIndex] = { partNumber, etag }
            uploadedBytes.value += chunk.size
            delete partProgressBytes.value[partNumber]
            updateUploadProgress()
        }
    }

    const workersCount = Math.min(PART_UPLOAD_CONCURRENCY, parts.length)
    const workers = Array.from({ length: workersCount }, () => uploadNext())
    await Promise.all(workers)

    return completedParts
}

const uploadPartWithRetry = async (url, chunk, mimeType, partNumber) => {
    let attempt = 0

    while (attempt < MAX_PART_RETRIES) {
        attempt += 1
        const controller = new AbortController()
        activePartAbortControllers.value.push(controller)

        try {
            const etag = await uploadPartWithXhr({
                url,
                chunk,
                mimeType,
                controller,
                onProgress: (loadedBytes) => {
                    partProgressBytes.value[partNumber] = loadedBytes
                    updateUploadProgress()
                }
            })

            return etag
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error
            }

            if (attempt >= MAX_PART_RETRIES) {
                throw error
            }
        } finally {
            activePartAbortControllers.value = activePartAbortControllers.value.filter((c) => c !== controller)
        }
    }
}

const uploadPartWithXhr = ({ url, chunk, mimeType, controller, onProgress }) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', url, true)
        xhr.setRequestHeader('Content-Type', mimeType)

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress(event.loaded)
            }
        }

        xhr.onload = () => {
            if (xhr.status < 200 || xhr.status >= 300) {
                reject(new Error(`Part upload failed with status ${xhr.status}.`))
                return
            }

            const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag')
            if (!etag) {
                reject(new Error('Missing ETag in part upload response.'))
                return
            }

            resolve(etag)
        }

        xhr.onerror = () => reject(new Error('Network error during part upload.'))
        xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'))

        controller.signal.addEventListener('abort', () => xhr.abort(), { once: true })
        xhr.send(chunk)
    })
}

const finalizeMultipartUpload = async (payload) => {
    try {
        await useAxiosPost('media', payload)
        uploadProgress.value = 100
        finalizeFailed.value = false
        pendingCompletePayload.value = null
        currentUploadSession.value = { uploadId: null, key: null }
    } catch (error) {
        finalizeFailed.value = true
        uploadLoading.value = false
        uploadPhase.value = 'finalizing'
        throw new Error(error?.message || 'Finalizing failed')
    }
}

const retryFinalizeUpload = async () => {
    if (!pendingCompletePayload.value) {
        return
    }

    try {
        uploadLoading.value = true
        uploadPhase.value = 'finalizing'
        await finalizeMultipartUpload(pendingCompletePayload.value)
        await handleUploadSuccess()
    } catch (error) {
        errorStore.showError('Finalizing failed',true,'error')
    } finally {
        if (!finalizeFailed.value) {
            uploadLoading.value = false
        }
    }
}

const cancelMultipartUpload = async () => {
    activePartAbortControllers.value.forEach((controller) => controller.abort())
    activePartAbortControllers.value = []
    partProgressBytes.value = {}

    const { uploadId, key } = currentUploadSession.value || {}
    if (!uploadId || !key) {
        return
    }

    try {
        await useAxiosPost('media', {
            uploadStage: 'abort',
            uploadId,
            key
        })
    } catch (error) {
        console.log('Abort upload failed:', error)
    } finally {
        currentUploadSession.value = { uploadId: null, key: null }
    }
}

const handleUploadSuccess = async () => {
    await getMyVideos()
    resetUploadState()
    emit('close', 'upload', false)
}

const resetUploadState = () => {
    uploadLoading.value = false
    uploadPhase.value = 'idle'
    uploadProgress.value = 0
    uploadedBytes.value = 0
    totalBytes.value = 0
    finalizeFailed.value = false
    pendingCompletePayload.value = null
    currentUploadSession.value = { uploadId: null, key: null }
    activePartAbortControllers.value = []
    partProgressBytes.value = {}
    file.value = null
    title.value = ''
}

const updateUploadProgress = () => {
    if (!totalBytes.value) {
        uploadProgress.value = 0
        return
    }

    const inflightBytes = Object.values(partProgressBytes.value).reduce((sum, value) => sum + Number(value || 0), 0)
    const totalUploaded = Math.min(totalBytes.value, uploadedBytes.value + inflightBytes)
    uploadProgress.value = Math.min(99, Math.floor((totalUploaded / totalBytes.value) * 100))
}

const uploadPhaseLabel = computed(() => {
    if (finalizeFailed.value) {
        return 'Finalizing'
    }

    if (uploadPhase.value === 'uploading') {
        return 'Uploading'
    }

    if (uploadPhase.value === 'finalizing') {
        return 'Finalizing'
    }

    return 'Uploading'
})

const uploadStatusText = computed(() => {
    if (uploadPhase.value === 'finalizing' || finalizeFailed.value) {
        return 'Finalizing upload...'
    }

    if (!totalBytes.value) {
        return 'Preparing upload...'
    }

    const inflightBytes = Object.values(partProgressBytes.value).reduce((sum, value) => sum + Number(value || 0), 0)
    const totalUploaded = Math.min(totalBytes.value, uploadedBytes.value + inflightBytes)
    return `${formatFileSize(totalUploaded)} of ${formatFileSize(totalBytes.value)} uploaded`
})

function formatFileSize(bytes) {
  const MB = bytes / (1024 * 1024);
  if (MB < 1) {
    const KB = bytes / 1024;
    return KB.toFixed(2) + ' KB';
  }
  return MB.toFixed(2) + ' MB';
}

const truncateString = (str, firstCharCount = str.length, endCharCount = 0, dotCount = 3) => {
  if (str.length <= firstCharCount + endCharCount) {
    return str; // No truncation needed
  }

  const firstPortion = str.slice(0, firstCharCount);
  const endPortion = str.slice(-endCharCount);
  const dots = '.'.repeat(dotCount);

  return `${firstPortion}${dots}${endPortion}`;
}

const removeFile = () =>{
    if (uploadLoading.value) {
        return
    }
    file.value = null;
}
</script>
